import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { rupee, apiError } from "@/lib/api";
import { loadRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Spinner, EmptyState } from "@/components/States";
import { Wallet, Clock, TrendingUp, Copy, Share2, Link2, BadgeCheck, Hourglass, Ban, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  approved: { label: "Approved", cls: "bg-brand-soft text-brand", icon: BadgeCheck },
  pending: { label: "Pending review", cls: "bg-amber-50 text-amber-warm", icon: Hourglass },
  rejected: { label: "Rejected", cls: "bg-red-50 text-red-500", icon: Ban },
  suspended: { label: "Suspended", cls: "bg-red-50 text-red-500", icon: Ban },
};

export default function AffiliateDashboard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const { data: me } = useQuery({ queryKey: ["aff-me"], queryFn: () => api.get("/affiliate/me").then((r) => r.data), refetchInterval: 10000 });
  const [verifying, setVerifying] = useState(false);
  const autoTriggered = useRef(false);

  const verifyBank = async () => {
    setVerifying(true);
    try {
      const { data: init } = await api.post("/affiliate/verify/create");
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway"); setVerifying(false); return; }
      const rzp = new window.Razorpay({
        key: init.key_id, amount: init.amount, currency: "INR", order_id: init.rzp_order_id,
        name: "Grofkart", description: `Influencer bank verification (₹${init.fee})`,
        prefill: { name: user?.name, email: user?.email, contact: user?.phone }, theme: { color: "#EC4899" },
        handler: async (resp) => {
          try { await api.post("/affiliate/verify/pay", resp); toast.success("Bank account verified!"); qc.invalidateQueries({ queryKey: ["aff-me"] }); }
          catch (e) { toast.error(apiError(e)); }
        },
        modal: { ondismiss: () => { setVerifying(false); toast.error("Verification cancelled"); } },
      });
      rzp.open();
    } catch (e) { toast.error(apiError(e)); setVerifying(false); }
  };

  useEffect(() => {
    if (me && !me.verified && params.get("verify") === "1" && !autoTriggered.current) {
      autoTriggered.current = true;
      setParams({}, { replace: true });
      verifyBank();
    }
  }, [me]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!me) return <DashboardShell title="Affiliate Program" roleColor="#EC4899" roleLabel="Influencer"><div className="flex justify-center py-20"><Spinner className="h-8 w-8" /></div></DashboardShell>;

  const link = `${window.location.origin}/?ref=${me.affiliate_code}`;
  const badge = STATUS_BADGE[me.status] || STATUS_BADGE.pending;

  const copy = (text, msg) => { navigator.clipboard.writeText(text); toast.success(msg || "Copied!"); };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: "Shop on Grofkart", url: link }); } catch { /* noop */ } }
    else copy(link, "Referral link copied!");
  };

  return (
    <DashboardShell title="Affiliate Program" roleColor="#EC4899" roleLabel="Influencer">
      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="affiliate-status-badge">
        <span className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-bold ${badge.cls}`}>
          <badge.icon className="h-3.5 w-3.5" /> {badge.label}
        </span>
        <span className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-bold ${me.verified ? "bg-brand-soft text-brand" : "bg-slate-100 text-slate-500"}`} data-testid="affiliate-verify-badge">
          {me.verified ? <ShieldCheck className="h-3.5 w-3.5" /> : <ShieldAlert className="h-3.5 w-3.5" />} {me.verified ? "Bank verified" : "Bank not verified"}
        </span>
        {me.status === "pending" && <span className="text-xs text-slate-500">You can share your link now — commissions start earning once an admin approves your account.</span>}
      </div>

      {!me.verified && (
        <div className="mb-6 flex flex-col items-start justify-between gap-3 rounded-2xl border border-pink-200 bg-pink-50/60 p-5 sm:flex-row sm:items-center" data-testid="affiliate-verify-banner">
          <div>
            <h3 className="flex items-center gap-2 font-head font-bold text-slate-900"><ShieldAlert className="h-5 w-5 text-pink-600" /> Verify your bank account</h3>
            <p className="mt-1 text-sm text-slate-600">Complete a one-time <b>₹{me.verification_fee}</b> bank verification to enable payouts to your account.</p>
          </div>
          <button onClick={verifyBank} disabled={verifying} data-testid="affiliate-verify-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-pink-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-pink-700 disabled:opacity-60">
            {verifying && <Spinner className="h-4 w-4 border-white" />} Verify now (₹{me.verification_fee})
          </button>
        </div>
      )}

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard testid="aff-wallet-balance" label="Available balance" value={rupee(me.wallet_balance)} icon={Wallet} color="#059669" />
        <StatCard testid="aff-wallet-pending" label="Pending (undelivered)" value={rupee(me.wallet_pending)} icon={Clock} color="#F59E0B" />
        <StatCard label="Lifetime earnings" value={rupee(me.wallet_lifetime)} icon={TrendingUp} color="#8B5CF6" />
        <StatCard label="Referred orders" value={me.referred_orders} icon={Link2} color="#3B82F6" />
      </div>

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="affiliate-link-card">
        <h3 className="mb-1 flex items-center gap-2 font-head font-bold text-slate-900"><Share2 className="h-5 w-5 text-brand" /> Your referral link</h3>
        <p className="mb-3 text-xs text-slate-500">Share this link. Any order placed by someone who arrives through it earns you a commission after delivery.</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="flex flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5">
            <Link2 className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="truncate text-sm text-slate-700" data-testid="affiliate-link-text">{link}</span>
          </div>
          <button onClick={() => copy(link, "Referral link copied!")} data-testid="affiliate-copy-link" className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Copy className="h-4 w-4" /> Copy</button>
          <button onClick={share} data-testid="affiliate-share-link" className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-brand px-4 py-2.5 text-sm font-bold text-white hover:bg-brand-deep"><Share2 className="h-4 w-4" /> Share</button>
        </div>
        <p className="mt-3 text-xs text-slate-500">Your code: <span className="cursor-pointer font-mono font-bold text-brand" onClick={() => copy(me.affiliate_code, "Code copied!")}>{me.affiliate_code}</span></p>
      </div>

      <Tabs defaultValue="earnings" className="flex flex-col gap-5 lg:flex-row lg:items-start">
        <TabsList className="flex h-auto w-full flex-row flex-wrap gap-1 rounded-2xl border border-slate-200 bg-white p-2 lg:sticky lg:top-24 lg:w-48 lg:shrink-0 lg:flex-col lg:items-stretch lg:[&>button]:w-full lg:[&>button]:justify-start [&>button[data-state=active]]:bg-brand-soft [&>button[data-state=active]]:text-brand">
          <TabsTrigger value="earnings" data-testid="aff-tab-earnings">Earnings</TabsTrigger>
          <TabsTrigger value="brands" data-testid="aff-tab-brands">Affiliate Brands</TabsTrigger>
          <TabsTrigger value="withdrawals" data-testid="aff-tab-withdrawals">Withdrawals</TabsTrigger>
          <TabsTrigger value="payout" data-testid="aff-tab-payout">Payout details</TabsTrigger>
          <TabsTrigger value="rates" data-testid="aff-tab-rates">Category rates</TabsTrigger>
        </TabsList>
        <div className="min-w-0 flex-1">
          <TabsContent value="earnings"><Earnings /></TabsContent>
          <TabsContent value="brands"><AffiliateBrands /></TabsContent>
          <TabsContent value="withdrawals"><Withdrawals me={me} /></TabsContent>
          <TabsContent value="payout"><Payout me={me} /></TabsContent>
          <TabsContent value="rates"><Rates /></TabsContent>
        </div>
      </Tabs>
    </DashboardShell>
  );
}

const Table = ({ children }) => <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white"><table className="w-full text-sm">{children}</table></div>;
const Th = ({ children }) => <th className="px-4 py-3 text-left text-xs font-bold uppercase text-slate-400">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const EARN_BADGE = { credited: "bg-brand-soft text-brand", pending: "bg-amber-50 text-amber-warm", cancelled: "bg-red-50 text-red-500" };

function Earnings() {
  const { data } = useQuery({ queryKey: ["aff-earnings"], queryFn: () => api.get("/affiliate/earnings").then((r) => r.data) });
  if (!data) return <Spinner />;
  if (!data.length) return <EmptyState icon={TrendingUp} title="No earnings yet" subtitle="Share your link — commissions appear here when your referrals order." />;
  return (
    <Table>
      <thead><tr className="border-b border-slate-100"><Th>Order</Th><Th>Commission</Th><Th>Status</Th><Th>Date</Th></tr></thead>
      <tbody data-testid="aff-earnings-table">
        {data.map((e) => (
          <tr key={e.id} className="border-b border-slate-50">
            <Td className="font-semibold text-slate-800">#{e.order_no}</Td>
            <Td className="font-mono text-slate-800">{rupee(e.amount)}</Td>
            <Td><span className={`rounded-md px-2 py-0.5 text-[11px] font-bold capitalize ${EARN_BADGE[e.status] || "bg-slate-100 text-slate-600"}`}>{e.status}</span></Td>
            <Td className="text-slate-500">{(e.created_at || "").slice(0, 10)}</Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function Withdrawals({ me }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["aff-withdrawals"], queryFn: () => api.get("/affiliate/withdrawals").then((r) => r.data) });
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const request = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post("/affiliate/withdrawals", { amount: amt });
      toast.success("Withdrawal requested! Awaiting admin approval.");
      setAmount("");
      qc.invalidateQueries({ queryKey: ["aff-withdrawals"] });
      qc.invalidateQueries({ queryKey: ["aff-me"] });
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const W_BADGE = { requested: "bg-amber-50 text-amber-warm", paid: "bg-brand-soft text-brand", rejected: "bg-red-50 text-red-500" };
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-1 font-head font-bold text-slate-900">Request a withdrawal</h3>
        <p className="mb-3 text-xs text-slate-500">Available balance: <b className="text-slate-800">{rupee(me.wallet_balance)}</b> · Minimum withdrawal: <b>{rupee(me.min_withdrawal)}</b></p>
        <div className="flex gap-2">
          <input value={amount} onChange={(e) => setAmount(e.target.value)} type="number" placeholder={`Amount (min ${me.min_withdrawal})`} data-testid="aff-withdraw-amount"
            className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand" />
          <button onClick={request} disabled={busy} data-testid="aff-withdraw-submit" className="rounded-xl bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-deep disabled:opacity-60">Request</button>
        </div>
        {!me.payout?.upi_id && !me.payout?.account_number && <p className="mt-2 text-xs text-red-500">Add your payout details first (Payout details tab).</p>}
      </div>
      {data && data.length > 0 && (
        <Table>
          <thead><tr className="border-b border-slate-100"><Th>Amount</Th><Th>Status</Th><Th>Requested</Th><Th>Note</Th></tr></thead>
          <tbody data-testid="aff-withdrawals-table">
            {data.map((w) => (
              <tr key={w.id} className="border-b border-slate-50">
                <Td className="font-mono text-slate-800">{rupee(w.amount)}</Td>
                <Td><span className={`rounded-md px-2 py-0.5 text-[11px] font-bold capitalize ${W_BADGE[w.status] || "bg-slate-100 text-slate-600"}`}>{w.status}</span></Td>
                <Td className="text-slate-500">{(w.created_at || "").slice(0, 10)}</Td>
                <Td className="text-slate-500">{w.note || "-"}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

function Payout({ me }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ method: me.payout?.method || "upi", upi_id: me.payout?.upi_id || "", account_name: me.payout?.account_name || "", account_number: me.payout?.account_number || "", ifsc: me.payout?.ifsc || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try { await api.patch("/affiliate/payout", form); toast.success("Payout details saved"); qc.invalidateQueries({ queryKey: ["aff-me"] }); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <div className="max-w-md space-y-3 rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="font-head font-bold text-slate-900">Payout details</h3>
      <div className="flex gap-2">
        {["upi", "bank"].map((m) => (
          <button key={m} onClick={() => setForm({ ...form, method: m })} data-testid={`aff-payout-method-${m}`}
            className={`flex-1 rounded-xl border px-3 py-2 text-sm font-semibold uppercase ${form.method === m ? "border-brand bg-brand-soft text-brand" : "border-slate-200 text-slate-600"}`}>{m}</button>
        ))}
      </div>
      {form.method === "upi" ? (
        <input value={form.upi_id} onChange={set("upi_id")} placeholder="UPI ID (e.g. name@upi)" data-testid="aff-payout-upi" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand" />
      ) : (
        <div className="space-y-2">
          <input value={form.account_name} onChange={set("account_name")} placeholder="Account holder name" data-testid="aff-payout-acc-name" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand" />
          <input value={form.account_number} onChange={set("account_number")} placeholder="Account number" data-testid="aff-payout-acc-number" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand" />
          <input value={form.ifsc} onChange={set("ifsc")} placeholder="IFSC code" data-testid="aff-payout-ifsc" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand" />
        </div>
      )}
      <button onClick={save} disabled={busy} data-testid="aff-payout-save" className="w-full rounded-xl bg-brand py-2.5 text-sm font-bold text-white hover:bg-brand-deep disabled:opacity-60">Save details</button>
    </div>
  );
}

function Rates() {
  const { data } = useQuery({ queryKey: ["aff-rates"], queryFn: () => api.get("/affiliate/config").then((r) => r.data) });
  const { data: cats } = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/categories").then((r) => r.data) });
  if (!data) return <Spinner />;
  const rates = data.rates || {};
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="mb-1 font-head font-bold text-slate-900">Category commission rates</h3>
      <p className="mb-3 text-xs text-slate-500">Applied when a product's brand is not in the affiliate brands list. Default rate: <b className="text-brand">{data.default_percent}%</b></p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {(cats || []).map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
            <span className="text-sm text-slate-700">{c.name}</span>
            <span className="font-mono text-sm font-bold text-brand">{rates[c.slug] ?? data.default_percent}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AffiliateBrands() {
  const { data } = useQuery({ queryKey: ["aff-brands"], queryFn: () => api.get("/affiliate/brands").then((r) => r.data) });
  if (!data) return <Spinner />;
  if (!data.length) return <EmptyState icon={BadgeCheck} title="No affiliate brands yet" subtitle="Brands enrolled in the affiliate program will appear here with their commission." />;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="mb-1 font-head font-bold text-slate-900">Brands available for affiliate</h3>
      <p className="mb-3 text-xs text-slate-500">Promote these brands to earn the listed commission on every referred order.</p>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2" data-testid="aff-brands-list">
        {data.map((b) => (
          <div key={b.id} className="flex items-center justify-between rounded-xl border border-pink-100 bg-pink-50/50 px-4 py-3">
            <span className="font-semibold text-slate-800">{b.brand}</span>
            <span className="rounded-lg bg-pink-600 px-2.5 py-1 font-mono text-sm font-bold text-white">{b.commission_percent}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
